import type { LinkProps } from '@tanstack/react-router';
import type { HTMLAttributes } from 'react';

import { Link } from '@tanstack/react-router';
import React, { useRef } from 'react';

import type { ButtonProps } from '../../../components/button';

import { Button } from '../../../components/button';
import { Separator } from '../../../components/separator';
import { useScreenSize } from '../../../hooks/use-screen-size';
import { cn } from '../../../lib/utils';
import { useHeaderIntersection } from '../hooks/use-header-intersection';
import { useStickyHeaderContext } from '../hooks/use-sticky-header-context';

// ============================================================================
// Section Root
// ============================================================================

export type SectionProps = HTMLAttributes<HTMLDivElement>;

export function SectionRoot({ className, ...props }: SectionProps) {
    return (
        <div className={cn('flex flex-col rounded-2xl bg-background-primary xl:border xl:border-border', className)} {...props} />
    );
}

// ============================================================================
// Section Header
// ============================================================================

export type SectionHeaderProps = HTMLAttributes<HTMLHeadingElement>;

export function SectionHeader({
    className,
    style,
    children,
    ...props
}: SectionHeaderProps) {
    const headerRef = useRef<HTMLDivElement>(null);
    const { isDesktop } = useScreenSize();

    // Get sticky offset from context (set by Page.Header height)
    const stickyContext = useStickyHeaderContext();
    const stickyTop = stickyContext?.stickyOffset ?? 0;

    // Only reserve space for fixed UI when there's NO Page.Header
    // If there's a Page.Header (stickyTop > 0), it already handles the fixed UI collision
    const hasPageHeader = stickyTop > 0;

    // Use intersection hook with the correct sticky position
    // Skip reservation entirely if there's a Page.Header
    const intersection = useHeaderIntersection(headerRef, {
        stickyTop,
        // Don't reserve space if Page.Header exists - it handles fixed UI collision
        skipReservation: hasPageHeader,
    });

    return (
        <div
            ref={headerRef}
            className="w-full sticky top-[var(--sticky-offset,0px)] z-10 flex flex-col gap-2.5 bg-background-primary rounded-t-2xl xl:shadow-header pt-2.5 xl:gap-4 xl:pt-4"
        >
            <div
                className={cn('flex flex-row justify-between items-center gap-2 px-6', className)}
                style={isDesktop
                    ? style
                    : {
                            paddingLeft: intersection.shouldReserveLeft
                                ? `${Math.max(intersection.paddingLeft, 24)}px` // 24px = px-6
                                : undefined,
                            paddingRight: intersection.shouldReserveRight
                                ? `${Math.max(intersection.paddingRight, 24)}px` // 24px = px-6
                                : undefined,
                            ...style,
                        }}
                {...props}
            >
                {children}
            </div>
            <Separator orientation="horizontal" />
        </div>
    );
}

// ============================================================================
// Section Header Title
// ============================================================================

export type SectionHeaderTitleProps = HTMLAttributes<HTMLHeadingElement>;

export function SectionHeaderTitle({ className, ...props }: SectionHeaderTitleProps) {
    return <h2 className={cn('text-xl leading-10 font-semibold truncate', className)} {...props} />;
}

// ============================================================================
// Section Header SubTitle
// ============================================================================

export type SectionHeaderSubTitleProps = HTMLAttributes<HTMLHeadingElement>;

export function SectionHeaderSubTitle({ className, ...props }: SectionHeaderSubTitleProps) {
    return (
        <h3 className={cn('text-sm font-semibold text-text-tertiary truncate', className)} {...props} />
    );
}

// ============================================================================
// Section Header Actions - Container for action buttons
// ============================================================================

export type SectionHeaderActionsProps = HTMLAttributes<HTMLDivElement>;

export function SectionHeaderActions({ className, ...props }: SectionHeaderActionsProps) {
    return (
        <div
            data-section-actions
            className={cn('flex items-center gap-1 shrink-0', className)}
            {...props}
        />
    );
}

// ============================================================================
// Section Header Action - Individual action button
// ============================================================================

export type SectionHeaderActionProps = {
    icon: React.ReactNode;
} & ButtonProps;

export function SectionHeaderAction({ icon, className, variant, size, outline, ...props }: SectionHeaderActionProps) {
    return (
        <Button variant={variant ?? 'ghost'} size={size ?? 'icon'} outline={outline} className={cn('', className)} {...props}>
            {icon}
        </Button>
    );
}

// ============================================================================
// Section Header ActionLink - Action button as a link
// ============================================================================

export type SectionHeaderActionLinkProps = {
    icon: React.ReactNode;
    className?: string;
} & Omit<LinkProps, 'children'>;

export function SectionHeaderActionLink({ icon, className, ...props }: SectionHeaderActionLinkProps) {
    return (
        <Button variant="ghost" size="icon" className={cn('', className)} asChild>
            <Link {...props}>
                {icon}
            </Link>
        </Button>
    );
}

// ============================================================================
// Section Content
// ============================================================================

export type SectionContentProps = HTMLAttributes<HTMLDivElement>;

export function SectionContent({ className, ...props }: SectionContentProps) {
    return (
        <div className={cn('flex flex-col gap-6 p-6', className)} {...props} />
    );
}

// ============================================================================
// Section Footer
// ============================================================================

export type SectionFooterProps = HTMLAttributes<HTMLDivElement>;
export function SectionFooter({ className, children, ...props }: SectionFooterProps) {
    return (
        <div className={cn('flex flex-col gap-2.5 xl:gap-4', className)} {...props}>
            <Separator orientation="horizontal" />
            <div className={cn('flex justify-between gap-6 p-6 pt-0 pb-2.5 xl:pb-4', className)} {...props}>
                {children}
            </div>
        </div>
    );
}

// ============================================================================
// Section Footer Link
// ============================================================================

export type SectionFooterLinkProps = LinkProps & {
    className?: string;
    variant?: ButtonProps['variant'];
};

export function SectionFooterLink({ className, variant, ...props }: SectionFooterLinkProps) {
    return (
        <Button variant={variant ?? 'ghost'} className={cn('', className)} asChild>
            <Link {...props} />
        </Button>
    );
}

// ============================================================================
// Section Footer Anchor Link
// ============================================================================

export type SectionFooterAnchorLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    className?: string;
    variant?: ButtonProps['variant'];
};

export function SectionFooterAnchorLink({ className, variant, ...props }: SectionFooterAnchorLinkProps) {
    return (
        <Button variant={variant ?? 'ghost'} className={cn('', className)} asChild>
            <a {...props} />
        </Button>
    );
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const Section = Object.assign(SectionRoot, {
    Header: Object.assign(SectionHeader, {
        Title: SectionHeaderTitle,
        SubTitle: SectionHeaderSubTitle,
        Actions: SectionHeaderActions,
        Action: SectionHeaderAction,
        ActionLink: SectionHeaderActionLink,
    }),
    Footer: Object.assign(SectionFooter, {
        Link: SectionFooterLink,
        AnchorLink: SectionFooterAnchorLink,
    }),
    /**
     * @deprecated Use Section.Header.Title instead
     */
    Title: SectionHeaderTitle,
    /**
     * @deprecated Use Section.Header.SubTitle instead
     */
    SubTitle: SectionHeaderSubTitle,
    Content: SectionContent,
});
